import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { useBookingState } from "../../hooks/useBookingState";
import {
  ReservationDto,
  ReservationShortlist,
} from "../../models/reservationShortlist.model";
import { BookingStateItem } from "../../reduxToolkit/reducers/bookingStateSlice";
import { complementaryService } from "../../service/complementary.service"; // Add this if you have a service
import { reservationShortlistService } from "../../service/reservationShortlist.service";
import { roomService } from "../../service/room.service";
import GalleryModal from "./GalleryModal";
import "./PropertyRoomDetail.css";

// Extend the Window interface to include __extrasOverlayRefs
declare global {
  interface Window {
    __extrasOverlayRefs?: HTMLElement[];
  }
}

interface Complementary {
  id: number;
  name: string;
  description?: string;
}

interface PropertyRoomDetailProps {
  propertyId: string | number;
  checkIn?: string;
  checkOut?: string;
  // roomTypeIds array for filtering rooms by multiple room types
  roomTypeIds?: number[];
  // Keep roomTypeId for backward compatibility (will be converted to roomTypeIds array internally)
  roomTypeId?: number | string;
  noOfAdults?: number;
  noOfChildrens?: number;
}

const PropertyRoomDetail: React.FC<PropertyRoomDetailProps> = ({
  propertyId,
  checkIn,
  checkOut,
  roomTypeIds,
  roomTypeId,
  noOfAdults,
  noOfChildrens,
}) => {
  // Props change effect (can be used for analytics/debug if needed)
  // useEffect(() => {
  //   ...existing code...
  // }, [noOfAdults, noOfChildrens, checkIn, checkOut, roomTypeId]);

  const [rooms, setRooms] = useState<any[]>([]);
  const [filteredRoomTypeName, setFilteredRoomTypeName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [complementaries, setComplementaries] = useState<Complementary[]>([]);
  const [roomExtras, setRoomExtras] = useState<number[][]>([]);
  const [saving, setSaving] = useState<number | null>(null);
  const [shortlistSaving, setShortlistSaving] = useState<number | null>(null);
  // Redux state management for reservations and shortlist
  const {
    getRoomState,
    addRoomReservation,
    addRoomToShortlist,
    removeRoomFromShortlist,
    isReserved,
    isShortlisted,
    initializeReservationState,
    initializeShortlistState,
  } = useBookingState();

  const [stateInitialized, setStateInitialized] = useState(false);
  const [complementariesModal, setComplementariesModal] = useState<{
    visible: boolean;
    complementaries: Complementary[];
  }>({ visible: false, complementaries: [] });

  // Room Gallery Modal State
  const [roomGalleryModal, setRoomGalleryModal] = useState<{
    visible: boolean;
    images: Array<{
      id: number;
      imageUrl: string;
      type: string;
      name: string;
      category?: string;
    }>;
    initialImageIndex: number;
    roomData?: any;
  }>({
    visible: false,
    images: [],
    initialImageIndex: 0,
    roomData: null,
  });

  // Helper to format ISO date string to 'YYYY-MM-DD' for input[type="date"]
  const formatDateForInput = (dateStr?: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  };

  const [reservationModal, setReservationModal] = useState({
    visible: false,
    roomIdx: -1,
    noOfAdults: 1,
    noOfChildrens: 0,
    checkIn: formatDateForInput(checkIn),
    checkOut: formatDateForInput(checkOut),
  });

  // Always update modal dates from props when they change and modal is closed
  useEffect(() => {
    if (!reservationModal.visible) {
      setReservationModal((prev) => ({
        ...prev,
        checkIn: formatDateForInput(checkIn),
        checkOut: formatDateForInput(checkOut),
      }));
    }
  }, [checkIn, checkOut]);
  // ...existing code...
  const navigate = useNavigate();
  const userId = useSelector((state: any) => state.auth.userId) ?? "";
  const userRole = useSelector((state: any) => state.auth.userRole) ?? "";

  // Initialize state from API data on component mount
  useEffect(() => {
    const initializeBookingState = async () => {
      if (userId && userRole === "B2BUser" && !stateInitialized) {
        try {
          const [shortlistedItems, bookingHistory] = await Promise.all([
            reservationShortlistService.getMyShortlistedItems(),
            reservationShortlistService.getMyBookingHistory(),
          ]);
          const shortlistData = shortlistedItems.map((item) => ({
            roomId: item.roomId,
            propertyId: Number(propertyId),
            checkIn: checkIn || "",
            checkOut: checkOut || "",
            timestamp: Date.now(),
          }));
          const reservationData: BookingStateItem[] = [];
          bookingHistory.forEach((booking: any) => {
            if (booking.bookedRooms && Array.isArray(booking.bookedRooms)) {
              booking.bookedRooms.forEach((bookedRoom: any) => {
                const isReserved = [bookedRoom.status, booking.status].some(
                  (s) => s === "Reserved" || s === "reserved"
                );
                if (isReserved) {
                  const roomId =
                    bookedRoom.roomId || bookedRoom.room?.id || bookedRoom.id;
                  if (roomId) {
                    reservationData.push({
                      roomId,
                      propertyId: Number(propertyId),
                      checkIn: booking.checkIn || checkIn || "",
                      checkOut: booking.checkOut || checkOut || "",
                      timestamp: Date.now(),
                    });
                  }
                }
              });
            }
          });
          initializeShortlistState(shortlistData);
          initializeReservationState(reservationData);
          setStateInitialized(true);
        } catch (error) {
          console.error("Error initializing booking state:", error);
        }
      }
    };
    initializeBookingState();
  }, [
    userId,
    userRole,
    propertyId,
    checkIn,
    checkOut,
    stateInitialized,
    initializeShortlistState,
    initializeReservationState,
    setStateInitialized,
  ]);

  // Normalize API responses into arrays
  const normalizeArray = (data: any): any[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (data.data !== undefined) return normalizeArray(data.data);
    if (data.data?.$values !== undefined)
      return normalizeArray(data.data.$values);
    if (data.$values !== undefined) return normalizeArray(data.$values);
    if (data.result !== undefined) return normalizeArray(data.result);
    if (data.value !== undefined) return normalizeArray(data.value);
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.values)) return data.values;
    if (Array.isArray(data.$data)) return data.$data;
    return [];
  };

  // Helper to robustly extract a roomType id from different API shapes
  const getRoomTypeIdFromRoom = (room: any): number | null => {
    if (!room) return null;
    const candidates = [
      room.roomTypeId,
      room.roomType?.id,
      room.RoomType?.id,
      room.RoomTypeId,
      room.roomTypeId,
      room.typeId,
    ];
    for (const c of candidates) {
      if (c !== undefined && c !== null && c !== "") return Number(c);
    }
    return null;
  };

  const getRoomTypeNameFromRoom = (room: any): string => {
    if (!room) return "";
    const candidates = [
      room.roomType?.name,
      room.roomTypeName,
      room.RoomType?.name,
      room.RoomTypeName,
      room.typeName,
    ];
    for (const c of candidates) {
      if (c !== undefined && c !== null && String(c).trim() !== "")
        return String(c);
    }
    return "";
  };

  // Get complementary mappings for a room
  const getRoomComplementaryMappings = (room: any): any[] => {
    if (!room) return [];
    if (Array.isArray(room.complementaries)) return room.complementaries;
    if (Array.isArray(room.roomComplementaryMappings))
      return room.roomComplementaryMappings;
    if (room.complementaries?.$values)
      return normalizeArray(room.complementaries.$values);
    if (room.roomComplementaryMappings?.$values)
      return normalizeArray(room.roomComplementaryMappings.$values);
    if (room.additionalData?.RMCRoomComplementaries)
      return normalizeArray(room.additionalData.RMCRoomComplementaries);
    if (room.additionalData?.rmcRoomComplementaries)
      return normalizeArray(room.additionalData.rmcRoomComplementaries);
    if (room.data?.$values) return normalizeArray(room.data.$values);
    if (room.data && Array.isArray(room.data)) return room.data;
    return [];
  };

  // Re-sync state when rooms are loaded (in case initial sync happened before rooms were available)
  useEffect(() => {
    if (
      rooms.length > 0 &&
      userId &&
      userRole === "B2BUser" &&
      stateInitialized
    ) {
      const checkStateSync = async () => {
        try {
          const bookingHistory =
            await reservationShortlistService.getMyBookingHistory();
          const currentReservedRoomIds = new Set<number>();
          bookingHistory.forEach((booking: any) => {
            if (booking.bookedRooms && Array.isArray(booking.bookedRooms)) {
              booking.bookedRooms.forEach((bookedRoom: any) => {
                const isReserved = [bookedRoom.status, booking.status].some(
                  (s) => s === "Reserved" || s === "reserved"
                );
                if (isReserved) {
                  const roomId =
                    bookedRoom.roomId || bookedRoom.room?.id || bookedRoom.id;
                  if (roomId) currentReservedRoomIds.add(roomId);
                }
              });
            }
          });
          rooms.forEach((room) => {
            if (currentReservedRoomIds.has(room.id) && !isReserved(room.id)) {
              const bookingItem = {
                roomId: room.id,
                propertyId: Number(propertyId),
                checkIn: checkIn || "",
                checkOut: checkOut || "",
                timestamp: Date.now(),
              };
              addRoomReservation(bookingItem);
            }
          });
        } catch (error) {
          console.error("Error re-syncing state:", error);
        }
      };
      checkStateSync();
    }
  }, [
    rooms,
    userId,
    userRole,
    stateInitialized,
    isReserved,
    addRoomReservation,
    propertyId,
    checkIn,
    checkOut,
  ]);

  // ...existing code...

  // Fetch complementaries and rooms
  useEffect(() => {
    setLoading(true);

    // Normalize incoming roomTypeIds array (or convert single roomTypeId to array for backward compatibility)
    const normalizedRoomTypeIds: number[] = roomTypeIds && roomTypeIds.length > 0
      ? roomTypeIds.filter(id => id > 0)
      : (roomTypeId ? [Number(roomTypeId)].filter(id => id > 0) : []);

    // Function to fetch rooms with fallback logic
    const fetchRooms = async () => {
      try {
        // For B2B users, try the user-detailed endpoint first
        if (userRole === "B2BUser") {
          console.log("Attempting getRoomsByPropertyIdForUserDetailed for B2B user");
          return await roomService.getRoomsByPropertyIdForUserDetailed(Number(propertyId));
        }
        
        // For non-B2B users, check if we have any valid roomTypeIds
        // If multiple room types are selected, we'll fetch all rooms and filter client-side
        if (normalizedRoomTypeIds.length === 1) {
          // If only one room type is selected, use the specific API endpoint
          console.log("Using getRoomsByPropertyId with single roomTypeId:", normalizedRoomTypeIds[0]);
          return await roomService.getRoomsByPropertyId(
            Number(propertyId),
            normalizedRoomTypeIds[0]
          );
        } else {
          // If multiple or no room types selected, fetch all rooms and filter client-side
          console.log("Using getByPropertyId (will filter client-side)");
          return await roomService.getByPropertyId(Number(propertyId));
        }
      } catch (error: any) {
        // If any error occurs (401, 404, etc.), fallback to the basic getByPropertyId endpoint
        console.warn("Error fetching rooms with primary method:", error);
        console.log("Falling back to getByPropertyId");
        
        // Fallback: fetch all rooms for the property using the basic endpoint
        return await roomService.getByPropertyId(Number(propertyId));
      }
    };

    console.log("Fetching rooms data:", {
      userRole,
      propertyId,
      roomTypeIds,
      roomTypeId,
      normalizedRoomTypeIds,
    });

    Promise.all([
      fetchRooms(),
      complementaryService.getAll().catch(() => ({ $values: [] })),
    ])
      .then(([roomData, compData]) => {
        console.log("Room data response:", roomData);

        // Normalize room list and complementary list into arrays
        const roomList = normalizeArray(roomData);
        const compsRaw = normalizeArray(compData);

        console.log("Normalized room list:", roomList);
        console.log("Room count:", roomList.length);

        // Normalize complementary objects (ensure numeric ids)
        const comps: Complementary[] = compsRaw.map((item: any) => ({
          id: typeof item.id === "string" ? Number(item.id) : item.id,
          name: item.name,
          description: item.description,
        }));

        // If roomTypeIds filter was provided, apply client-side filtering
        if (normalizedRoomTypeIds.length > 0) {
          const filtered = roomList.filter((r: any) => {
            const rid = getRoomTypeIdFromRoom(r);
            return rid !== null && normalizedRoomTypeIds.includes(Number(rid));
          });
          setRooms(filtered);
          
          // Build a friendly name showing all filtered room types
          if (normalizedRoomTypeIds.length === 1) {
            // Single room type - show its name
            const first = filtered[0] || roomList.find(
              (r: any) => getRoomTypeIdFromRoom(r) === normalizedRoomTypeIds[0]
            );
            const rtName = first ? getRoomTypeNameFromRoom(first) : "";
            setFilteredRoomTypeName(rtName);
          } else {
            // Multiple room types - show count or names
            const roomTypeNames = filtered.map(r => getRoomTypeNameFromRoom(r)).filter(Boolean);
            const uniqueRoomTypeNames = Array.from(new Set(roomTypeNames));
            const displayName = uniqueRoomTypeNames.length <= 3 
              ? uniqueRoomTypeNames.join(", ")
              : `${uniqueRoomTypeNames.length} Room Types`;
            setFilteredRoomTypeName(displayName);
          }
        } else {
          setRooms(roomList);
          setFilteredRoomTypeName("");
        }
        setComplementaries(comps);

        // Build per-room complementaries mapping safely
        const extrasPerRoom: number[][] = roomList.map((room: any) => {
          const mappings = getRoomComplementaryMappings(room) || [];
          return mappings
            .map(
              (rcm: any) =>
                rcm.complementary?.id ?? rcm.id ?? rcm.complementaryId
            )
            .filter((id: any) => id !== undefined && id !== null)
            .map((id: any) => Number(id));
        });

        setRoomExtras(extrasPerRoom);
      })
      .catch((err) =>
        console.error("Failed to load rooms or complementaries", err)
      )
      .finally(() => setLoading(false));
  }, [propertyId, roomTypeIds, roomTypeId, userRole]);

  // PATCH/Save mapping
  const handleReserve = async (roomIdx: number) => {
    const room = rooms[roomIdx];

    // Check room state before attempting reservation
    const roomState = getRoomDetailedState(room?.id);

    // If room is already reserved, show appropriate message
    if (roomState.isReserved) {
      Swal.fire({
        icon: "info",
        title: "Already Reserved",
        text: "This room is already reserved.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    // Check if user is B2BUser and use reserveRoom method
    if (userRole === "B2BUser") {
      if (!room?.id) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Missing required information for reservation",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
        return;
      }

      // Show modal to collect adults and children count
      const calculatedAdults =
        noOfAdults !== undefined && noOfAdults !== null && noOfAdults > 0
          ? noOfAdults
          : 1;
      const calculatedChildren =
        noOfChildrens !== undefined && noOfChildrens !== null
          ? noOfChildrens
          : 0;

      console.log("Setting reservation modal:", {
        noOfAdultsFromProps: noOfAdults,
        noOfChildrensFromProps: noOfChildrens,
        checkInFromProps: checkIn,
        checkOutFromProps: checkOut,
        calculatedAdults,
        calculatedChildren,
        userRole,
      });

      setReservationModal({
        visible: true,
        roomIdx,
        noOfAdults: calculatedAdults,
        noOfChildrens: calculatedChildren,
        checkIn: formatDateForInput(checkIn),
        checkOut: formatDateForInput(checkOut),
      });
    } else {
      // For non-B2BUser, navigate to reserve page as before
      navigate("/reserve", {
        state: {
          propertyId,
          roomId: room?.id,
          checkIn,
          checkOut,
          roomName: room?.name,
          // Pass room images so reserve page can display real gallery
          roomImages: room?.roomImages?.$values ?? room?.roomImages ?? [],
        },
      });
    }
  };

  // Handle actual reservation after collecting adults/children
  const handleConfirmReservation = async () => {
    const room = rooms[reservationModal.roomIdx];

    // Validate that we have check-in and check-out dates from modal state
    const modalCheckIn = reservationModal.checkIn || "";
    const modalCheckOut = reservationModal.checkOut || "";
    if (!modalCheckIn || !modalCheckOut) {
      Swal.fire({
        icon: "error",
        title: "Missing Dates",
        text: "Check-in and check-out dates are required for reservation.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 4000,
      });
      return;
    }

    // Validate date formats
    const checkInDate = new Date(modalCheckIn);
    const checkOutDate = new Date(modalCheckOut);

    if (isNaN(checkInDate.getTime()) || isNaN(checkOutDate.getTime())) {
      Swal.fire({
        icon: "error",
        title: "Invalid Dates",
        text: "Please provide valid check-in and check-out dates.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 4000,
      });
      return;
    }

    setSaving(reservationModal.roomIdx);

    try {
      const reservationData: ReservationDto = {
        checkIn: checkInDate,
        checkOut: checkOutDate,
        roomId: room.id,
        propertyId: Number(propertyId),
        noOfAdults: reservationModal.noOfAdults,
        noOfChildrens: reservationModal.noOfChildrens,
      };

      console.log("Reservation data being sent:", {
        reservationData,
        checkInOriginal: modalCheckIn,
        checkOutOriginal: modalCheckOut,
        checkInParsed: checkInDate,
        checkOutParsed: checkOutDate,
        noOfAdults: reservationModal.noOfAdults,
        noOfChildrens: reservationModal.noOfChildrens,
      });

      await reservationShortlistService.reserveRoom(reservationData);

      // Navigate to thank you page
      navigate("/reservation-thank-you");

      // Add room to Redux reserved state
      const bookingItem = {
        roomId: room.id,
        propertyId: Number(propertyId),
        checkIn: modalCheckIn,
        checkOut: modalCheckOut,
        timestamp: Date.now(),
      };
      addRoomReservation(bookingItem);

      // Also update the room's isShortlisted property
      setRooms((prevRooms) =>
        prevRooms.map((r) =>
          r.id === room.id ? { ...r, isShortlisted: false } : r
        )
      );

      // Close modal
      setReservationModal({
        visible: false,
        roomIdx: -1,
        noOfAdults: 1,
        noOfChildrens: 0,
        checkIn: checkIn || "",
        checkOut: checkOut || "",
      });
    } catch (error: any) {
      console.error("Error reserving room:", error);

      // Handle specific error messages from backend
      let errorMessage = "Failed to reserve room. Please try again.";

      if (error?.response?.data) {
        // Check for duplicate booking message
        if (error.response.data.includes("already booked")) {
          errorMessage = "This room is already booked by another user.";
        }
        // Use backend message if available
        else if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        }
        // Handle structured error response
        else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }

      Swal.fire({
        icon: "error",
        title: "Reservation Failed",
        text: errorMessage,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 4000,
      });
    } finally {
      setSaving(null);
    }
  };

  // Show complementaries in a modal
  const handleShowComplementaries = (extras: number[]) => {
    const selectedExtras = complementaries.filter((c) => extras.includes(c.id));
    setComplementariesModal({
      visible: true,
      complementaries: selectedExtras,
    });
  };

  // Resolve complementaries for a room by merging global list with RMC complementaries present on the room
  const resolveComplementariesForRoom = (
    room: any,
    idx: number
  ): Complementary[] => {
    const ids = (roomExtras[idx] || []) as number[];
    const matched = complementaries.filter((c) => ids.includes(c.id));
    if (matched.length > 0) return matched;

    // fallback: use additionalData.rmcRoomComplementaries or RMCRoomComplementaries directly from room
    const rmcList =
      room?.additionalData?.rmcRoomComplementaries ??
      room?.additionalData?.RMCRoomComplementaries ??
      getRoomComplementaryMappings(room);
    if (Array.isArray(rmcList) && rmcList.length > 0) {
      return rmcList.map(
        (r: any) =>
          ({
            id: Number(r.id),
            name: r.name || r.displayName || "",
          } as Complementary)
      );
    }

    // final fallback: if we have ids but no matching global items, return id-only objects
    if (ids.length > 0) {
      return ids.map((i) => ({ id: i, name: `Extra ${i}` } as Complementary));
    }

    return [];
  };

  const showComplementariesForRoom = (room: any, idx: number) => {
    const comps = resolveComplementariesForRoom(room, idx);
    setComplementariesModal({ visible: true, complementaries: comps });
  };

  // Open Room Gallery Modal - Show only selected room's images
  const openRoomGallery = (
    clickedRoomIndex: number,
    clickedImageIndex: number = 0
  ) => {
    const selectedRoom = rooms[clickedRoomIndex];
    if (!selectedRoom) return;

    console.log("openRoomGallery - selectedRoom:", selectedRoom);

    const roomImages = getRoomImages(selectedRoom);
    const roomTypeName = getRoomTypeName(selectedRoom);
    const categoryName = getCategoryName(selectedRoom) || "Room";
    const roomName = selectedRoom?.name || `Room ${clickedRoomIndex + 1}`;
    const roomDescription = getRoomDescription(selectedRoom);

    console.log("openRoomGallery - extracted description:", roomDescription);

    // Create image objects for only this room
    const roomImageData: Array<{
      id: number;
      imageUrl: string;
      type: string;
      name: string;
      category?: string;
    }> = roomImages.map((imageUrl, imgIdx) => ({
      id: imgIdx,
      imageUrl,
      type: roomTypeName,
      name: roomName,
      category: categoryName,
    }));

    // Create enhanced room data with extracted description
    const enhancedRoomData = {
      ...selectedRoom,
      description: roomDescription,
    };

    console.log("openRoomGallery - enhancedRoomData:", enhancedRoomData);

    setRoomGalleryModal({
      visible: true,
      images: roomImageData,
      initialImageIndex: clickedImageIndex,
      roomData: enhancedRoomData,
    });
  };

  // Close Room Gallery Modal
  const closeRoomGallery = () => {
    setRoomGalleryModal({
      visible: false,
      images: [],
      initialImageIndex: 0,
      roomData: null,
    });
  };

  // Manual sync function to force refresh of booking state
  const forceRefreshBookingState = async () => {
    try {
      console.log("Force refreshing booking state...");
      setStateInitialized(false);

      const [shortlistedItems, bookingHistory] = await Promise.all([
        reservationShortlistService.getMyShortlistedItems(),
        reservationShortlistService.getMyBookingHistory(),
      ]);

      // Clear existing state and reinitialize
      const shortlistData: BookingStateItem[] = shortlistedItems.map(
        (item) => ({
          roomId: item.roomId,
          propertyId: Number(propertyId),
          checkIn: checkIn || "",
          checkOut: checkOut || "",
          timestamp: Date.now(),
        })
      );

      const reservationData: BookingStateItem[] = [];
      bookingHistory.forEach((booking: any) => {
        if (booking.bookedRooms && Array.isArray(booking.bookedRooms)) {
          booking.bookedRooms.forEach((bookedRoom: any) => {
            const isReserved =
              bookedRoom.status === "Reserved" ||
              bookedRoom.status === "reserved" ||
              booking.status === "Reserved" ||
              booking.status === "reserved";

            if (isReserved) {
              const roomId =
                bookedRoom.roomId || bookedRoom.room?.id || bookedRoom.id;
              if (roomId) {
                reservationData.push({
                  roomId: roomId,
                  propertyId: Number(propertyId),
                  checkIn: booking.checkIn || checkIn || "",
                  checkOut: booking.checkOut || checkOut || "",
                  timestamp: Date.now(),
                });
              }
            }
          });
        }
      });

      initializeShortlistState(shortlistData);
      initializeReservationState(reservationData);
      setStateInitialized(true);

      console.log("Booking state refreshed:", {
        shortlistData,
        reservationData,
      });
    } catch (error) {
      console.error("Error refreshing booking state:", error);
    }
  };

  // Helper function to get detailed room state information
  const getRoomDetailedState = (roomId: number) => {
    const reserved = isReserved(roomId);
    const shortlisted = isShortlisted(roomId);

    return {
      isReserved: reserved,
      isShortlisted: shortlisted,
      canReserve: !reserved && !shortlisted, // Can only reserve if not already reserved or shortlisted
      canAddToShortlist: !reserved && !shortlisted, // Can only shortlist if not reserved or already shortlisted
      canRemoveFromShortlist: shortlisted && !reserved, // Can only remove from shortlist if it's actually shortlisted
      status: reserved ? "reserved" : shortlisted ? "shortlisted" : "available",
      statusMessage: reserved
        ? "This room is already reserved"
        : shortlisted
        ? "This room is in your shortlist"
        : "This room is available",
    };
  };

  // Handle shortlist functionality
  const handleShortlist = async (roomIdx: number) => {
    const room = rooms[roomIdx];
    if (!room?.id || !checkIn || !checkOut) return;

    // Check if user is authenticated
    if (!userId || userId.trim() === "") {
      Swal.fire({
        icon: "error",
        title: "Authentication Required",
        text: "Please log in to manage your shortlist.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    // Check room state before making API calls
    const roomState = getRoomDetailedState(room.id);
    const roomIsShortlisted = roomState.isShortlisted || room?.isShortlisted;

    // If room is already reserved, don't allow shortlisting
    if (roomState.isReserved) {
      Swal.fire({
        icon: "warning",
        title: "Cannot Add to Shortlist",
        text: "This room is already reserved and cannot be added to your shortlist.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 4000,
      });
      return;
    }

    // If trying to add to shortlist but already shortlisted
    if (!roomIsShortlisted) {
      // Check if room is already in shortlist (additional server-side verification)
      try {
        const currentShortlist =
          await reservationShortlistService.getMyShortlistedItems();
        const alreadyInShortlist = currentShortlist.some(
          (item) => item.roomId === room.id
        );

        if (alreadyInShortlist) {
          Swal.fire({
            icon: "info",
            title: "Already in Shortlist",
            text: "This room is already in your shortlist.",
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          });

          // Sync Redux state with server state
          const bookingItem = {
            roomId: room.id,
            propertyId: Number(propertyId),
            checkIn: checkIn || "",
            checkOut: checkOut || "",
            timestamp: Date.now(),
          };
          addRoomToShortlist(bookingItem);
          return;
        }
      } catch (error) {
        console.error("Error checking shortlist status:", error);
      }
    }

    setShortlistSaving(roomIdx);

    // Debug logging for state tracking
    console.log("Room shortlist operation:", {
      roomId: room.id,
      roomState: roomState,
      isCurrentlyShortlisted: roomIsShortlisted,
      action: roomIsShortlisted ? "remove" : "add",
    });

    // Now proceed with the actual shortlist operation
    const isCurrentlyShortlisted = roomIsShortlisted;

    try {
      if (isCurrentlyShortlisted) {
        // Remove from shortlist
        await reservationShortlistService.removeFromShortlist(room.id);

        // Update Redux state to remove room from shortlist
        removeRoomFromShortlist(room.id);

        // Also update the room's isShortlisted property if it exists
        setRooms((prevRooms) =>
          prevRooms.map((r) =>
            r.id === room.id ? { ...r, isShortlisted: false } : r
          )
        );

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Room removed from shortlist successfully!",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      } else {
        // Add to shortlist
        // Validate dates
        const checkInDate = new Date(checkIn);
        const checkOutDate = new Date(checkOut);

        if (isNaN(checkInDate.getTime()) || isNaN(checkOutDate.getTime())) {
          Swal.fire({
            icon: "error",
            title: "Invalid Dates",
            text: "Please select valid check-in and check-out dates.",
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }

        const reservationShortlistPayload: ReservationShortlist = {
          roomId: room.id,
          applicationUserId: userId,
          checkInDate: checkInDate,
          checkOutDate: checkOutDate,
          status: "Pending",
          priority: 0,
          proposalId:room.proposalId
        };

        const createResp =
          await reservationShortlistService.createReservationShortlist(
            reservationShortlistPayload
          );

        // If the service returned an ApiResponse explicitly signalling failure, bail
        if (createResp && createResp.success === false) {
          const msg = createResp.message || "Failed to add to shortlist.";
          Swal.fire({
            icon: "warning",
            title: "Shortlist Failed",
            text: msg,
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          });
          setShortlistSaving(null);
          return;
        }

        // Otherwise treat the resolved call as success (backend performed the action)
        const bookingItem = {
          roomId: room.id,
          propertyId: Number(propertyId),
          checkIn: checkIn || "",
          checkOut: checkOut || "",
          timestamp: Date.now(),
        };
        addRoomToShortlist(bookingItem);

        // Also update the room's isShortlisted property if it exists
        setRooms((prevRooms) =>
          prevRooms.map((r) =>
            r.id === room.id ? { ...r, isShortlisted: true } : r
          )
        );

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Room added to shortlist successfully!",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error: any) {
      console.error("Error managing shortlist:", error);

      let errorMessage = isCurrentlyShortlisted
        ? "Failed to remove room from shortlist. Please try again."
        : "Failed to add room to shortlist. Please try again.";

      if (error?.response?.data) {
        const errorData = error.response.data;

        // Check for various server-side error messages
        if (typeof errorData === "string") {
          if (errorData.includes("already in your shortlist")) {
            errorMessage = "This room is already in your shortlist.";
            // Sync Redux state with server state
            const bookingItem = {
              roomId: room.id,
              propertyId: Number(propertyId),
              checkIn: checkIn || "",
              checkOut: checkOut || "",
              timestamp: Date.now(),
            };
            addRoomToShortlist(bookingItem);
          } else if (
            errorData.includes("already booked") ||
            errorData.includes("already reserved")
          ) {
            errorMessage =
              "This room is already reserved and cannot be shortlisted.";
            // Sync Redux state - mark as reserved
            const bookingItem = {
              roomId: room.id,
              propertyId: Number(propertyId),
              checkIn: checkIn || "",
              checkOut: checkOut || "",
              timestamp: Date.now(),
            };
            addRoomReservation(bookingItem);
          }
        }

        // Also handle object responses
        if (errorData?.message) {
          if (errorData.message.includes("already in your shortlist")) {
            errorMessage = "This room is already in your shortlist.";
          } else if (
            errorData.message.includes("already booked") ||
            errorData.message.includes("already reserved")
          ) {
            errorMessage =
              "This room is already reserved and cannot be shortlisted.";
            // Sync state
            const bookingItem = {
              roomId: room.id,
              propertyId: Number(propertyId),
              checkIn: checkIn || "",
              checkOut: checkOut || "",
              timestamp: Date.now(),
            };
            addRoomReservation(bookingItem);
          }
        }
      }

      console.log("Final error message:", errorMessage);

      // Attempt to re-sync with server state: maybe backend succeeded but client request errored
      try {
        const currentShortlist =
          await reservationShortlistService.getMyShortlistedItems();
        const exists = currentShortlist.some(
          (item: any) => item.roomId === room.id
        );
        const bookingItem = {
          roomId: room.id,
          propertyId: Number(propertyId),
          checkIn: checkIn || "",
          checkOut: checkOut || "",
          timestamp: Date.now(),
        };

        if (exists && !isShortlisted(room.id)) {
          // Server shows the room as shortlisted -> sync client state
          addRoomToShortlist(bookingItem);
          setRooms((prevRooms) =>
            prevRooms.map((r) =>
              r.id === room.id ? { ...r, isShortlisted: true } : r
            )
          );
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Room added to shortlist.",
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }

        if (!exists && isCurrentlyShortlisted) {
          // Server doesn't have it -> ensure client reflects removal
          removeRoomFromShortlist(room.id);
          setRooms((prevRooms) =>
            prevRooms.map((r) =>
              r.id === room.id ? { ...r, isShortlisted: false } : r
            )
          );
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Room removed from shortlist (synced).",
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
      } catch (syncErr) {
        console.error(
          "Error syncing shortlist state after failed request:",
          syncErr
        );
      }

      // Fallback: show original error message
      Swal.fire({
        icon: "warning",
        title: isCurrentlyShortlisted
          ? "Cannot Remove from Shortlist"
          : "Cannot Add to Shortlist",
        text: errorMessage,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 4000,
      });
    } finally {
      setShortlistSaving(null);
    }
  };

  if (loading) {
    return <div>Loading rooms...</div>;
  }
  // Helper to extract image URL list from varied API shapes (array | { $values: [] })
  const getRoomImages = (room: any): string[] => {
    if (!room) return [];
    // if API provides a top-level imageUrl string, prefer that
    if (typeof room.imageUrl === "string" && room.imageUrl.trim() !== "") {
      return [room.imageUrl];
    }

    const imgsRaw =
      room.roomImages?.$values ??
      room.roomImages ??
      room.imageDtos?.$values ??
      room.imageDtos ??
      room.imageDtos?.ImageDtos ??
      room.images ??
      [];

    const imgs = Array.isArray(imgsRaw) ? imgsRaw : [];

    const result = imgs
      .map((i: any) => {
        if (!i) return null;
        // base64 image returned from server
        const imageByte =
          i.imageByte ??
          i.ImageByte ??
          i.imageBytes ??
          i.imageBase64 ??
          i.base64;
        const contentType = i.contentType ?? i.ContentType ?? i.contentTypeName;
        if (imageByte && contentType) {
          // ensure base64 string doesn't contain data: prefix already
          const base =
            typeof imageByte === "string" && imageByte.startsWith("data:")
              ? imageByte
              : `data:${contentType};base64,${imageByte}`;
          return base;
        }

        // common url/file name fields
        const fileName =
          i.imageUrl ??
          i.ImageUrl ??
          i.fileName ??
          i.FileName ??
          i.filePath ??
          i.url ??
          i.Url;
        if (!fileName) return null;

        const fileStr = String(fileName);
        if (/^https?:\/\//i.test(fileStr)) return fileStr;
        // If file already has leading slash, keep it
        if (fileStr.startsWith("/")) return fileStr;
        // Otherwise assume server serves images under /Images/
        return `/Images/${fileStr}`;
      })
      .filter(Boolean) as string[];

    if (result.length === 0) {
      // Helpful debug for missing images
      // eslint-disable-next-line no-console
      console.debug(
        "No images found for room",
        room.id ?? room.roomId ?? room.name,
        imgsRaw
      );
    }

    return result;
  };

  // Helper to extract room type name from varied API shapes
  const getRoomTypeName = (room: any): string => {
    if (!room) return "";
    const candidates = [
      room.roomType?.name,
      room.roomType?.Name,
      room.RoomType?.name,
      room.RoomType?.Name,
      room.roomTypeName,
      room.RoomTypeName,
      room.typeName,
      room.type,
      room.roomType?.displayName,
      room.RoomType?.displayName,
    ];
    for (const c of candidates) {
      if (c !== undefined && c !== null && String(c).trim() !== "")
        return String(c);
    }
    return "";
  };

  // Helper to extract category name
  const getCategoryName = (room: any): string => {
    if (!room) return "";
    const candidates = [
      room.category?.name,
      room.category?.Name,
      room.Category?.name,
      room.Category?.Name,
      room.categoryDescription,
      room.CategoryDescription,
    ];
    for (const c of candidates) {
      if (c !== undefined && c !== null && String(c).trim() !== "")
        return String(c);
    }
    return "";
  };

  // Helper to extract a description for the room
  const getRoomDescription = (room: any): string => {
    if (!room) return "";

    console.log("getRoomDescription - Full room object:", room);

    // First, try to get description from nested roomImages.room.description path
    const roomImagesData = room.roomImages?.$values ?? room.roomImages ?? [];
    console.log("getRoomDescription - roomImagesData:", roomImagesData);

    if (Array.isArray(roomImagesData) && roomImagesData.length > 0) {
      for (let i = 0; i < roomImagesData.length; i++) {
        const roomImage = roomImagesData[i];
        console.log(`getRoomDescription - roomImage[${i}]:`, roomImage);
        const nestedDescription = roomImage?.room?.description;
        console.log(
          `getRoomDescription - nested description[${i}]:`,
          nestedDescription
        );
        if (nestedDescription && String(nestedDescription).trim() !== "") {
          console.log(
            "getRoomDescription - Found nested description:",
            nestedDescription
          );
          return String(nestedDescription);
        }
      }
    }

    // Fallback to other description fields
    const candidates = [
      room.description,
      room.Description,
      room.shortDescription,
      room.ShortDescription,
      room.categoryDescription,
      room.CategoryDescription,
    ];

    console.log("getRoomDescription - Fallback candidates:", candidates);

    for (const c of candidates) {
      if (c !== undefined && c !== null && String(c).trim() !== "") {
        console.log("getRoomDescription - Found fallback description:", c);
        return String(c);
      }
    }

    console.log("getRoomDescription - No description found");
    return "";
  };

  return (
    <>
      <div className="p-3 border rounded-4 shadow-sm bg-white">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h5 className="fw-bold mb-0">Rooms Details</h5>
          {filteredRoomTypeName ? (
            <small className="text-muted">
              Showing: <strong>{filteredRoomTypeName}</strong>
            </small>
          ) : null}
        </div>
        <Swiper
          className="property-room-swiper"
          modules={[Navigation, Pagination]}
          spaceBetween={20}
          slidesPerView={3}
          navigation
          pagination={{ clickable: true }}
          breakpoints={{
            0: { slidesPerView: 1 },
            600: { slidesPerView: 1.2 },
            900: { slidesPerView: 2 },
            1200: { slidesPerView: 3 },
          }}
        >
          {rooms.map((room, idx) => {
            const title = room?.name || `Room ${idx + 1}`;
            const images = getRoomImages(room);
            const area = room?.area;
            // selectedExtras: prefer resolved complementaries (global list merged with RMC complementaries)
            const selectedExtras = resolveComplementariesForRoom(room, idx);

            // RMC override amount extraction (prefer RMC room charges if present)
  const getRmcAmountForRoom = (r: any): { amount: number | null, currencySymbol: string, isShowPrice: boolean } => {
              if (!r) return { amount: null, currencySymbol: '€', isShowPrice: true };
              let amount = null;
              let isShowPrice = true; // Default to showing price unless explicitly disabled
              
              // Use the isShowPrice value normalized by the service
              isShowPrice = r.isShowPrice !== undefined ? r.isShowPrice : true;
              
              if (Array.isArray(r.rmcRoomCharges) && r.rmcRoomCharges.length > 0) {
                const first = r.rmcRoomCharges[0];
                amount = first?.amount ?? null;
              }
              // support both PascalCase and camelCase keys in additionalData
              else if (r.additionalData && Array.isArray(r.additionalData.RMCRoomCharges) && r.additionalData.RMCRoomCharges.length > 0) {
                amount = r.additionalData.RMCRoomCharges[0].amount ?? null;
              }
              else if (r.additionalData && Array.isArray(r.additionalData.rmcRoomCharges) && r.additionalData.rmcRoomCharges.length > 0) {
                amount = r.additionalData.rmcRoomCharges[0].amount ?? null;
              }
              else if (r.amount !== undefined && r.amount !== null) {
                amount = Number(r.amount);
              }
              
              // Get currency symbol from room data, matching ShortlistStatic behavior
              const currencySymbol = r.currencySymbol || '€';              return { amount, currencySymbol, isShowPrice };
            };
            const { amount: displayAmount, currencySymbol, isShowPrice } = getRmcAmountForRoom(room);
            return (
              <SwiperSlide key={idx}>
                <div className="property-item h-100">
                  <article className="card hover-effect-opacity border d-flex flex-column mb-5">
                    <div className="card-img-top position-relative bg-body-tertiary overflow-hidden">
                      <div className="swiper z-2">
                        <Swiper
                          modules={[Navigation, Pagination]}
                          spaceBetween={10}
                          slidesPerView={1}
                          pagination={{ clickable: true }}
                          navigation
                        >
                          {images.length > 0 ? (
                            images.map((img, i) => (
                              <SwiperSlide key={i}>
                                <div
                                  className="ratio d-block image-anime"
                                  style={{ maxHeight: 260, cursor: "pointer" }}
                                  onClick={() => openRoomGallery(idx, i)}
                                >
                                  <img
                                    src={img}
                                    alt={title + " image " + (i + 1)}
                                    className="property-img image-anime img-thumbnail"
                                    style={{
                                      objectFit: "cover",
                                      width: "100%",
                                      height: "250px",
                                    }}
                                  />
                                  <span className="img-overlay"></span>
                                  {/* Gallery overlay icon */}
                                  <div className="gallery-overlay-icon">
                                    <i
                                      className="pi pi-eye"
                                      style={{
                                        fontSize: "1.5rem",
                                        color: "white",
                                      }}
                                    ></i>
                                  </div>
                                </div>
                              </SwiperSlide>
                            ))
                          ) : (
                            <SwiperSlide>
                              <div className="ratio d-block image-anime">
                                <img
                                  src="/Images/room-placeholder.jpg"
                                  alt="Room placeholder"
                                  className="property-img image-anime img-thumbnail"
                                  style={{
                                    objectFit: "cover",
                                    width: "100%",
                                    height: "250px",
                                  }}
                                />
                                <span className="img-overlay"></span>
                              </div>
                            </SwiperSlide>
                          )}
                          <div
                            className="swiper-pagination  mt-5px-3"
                            data-bs-theme="dark"
                          ></div>
                        </Swiper>
                      </div>
                    </div>
                    <div className="card-body py-2">
                      <h5 className="tx-18 _600 text-body mb-0">
                        {room.name || ""}
                      </h5>
                      <div className="mt-1 text-muted tx-13">
                        {getCategoryName(room) ? (
                          <span className="me-2">
                            • {getCategoryName(room)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="card-footer gap-2 border-0 bg-transparent pt-6 pb-6 px-6 mt-n4">
                      <div className="d-flex flex-column ">
                        <div className="row row-cols-1 row-cols-sm-1 gap-2">
                          <div className="col d-flex">
                            <i className="bi bi-arrows-fullscreen tx-14 "></i>
                            <span className="tx-13 px-2">
                              {" "}
                              {area ? `${area} sq ft` : ""}
                            </span>
                          </div>

                          <div className="col d-flex">
                            <i className="bi bi-door-open tx-14  "></i>
                            <span className="tx-13 px-2">
                              {getRoomTypeName(room) ? (
                                <span className="me-2">
                                  {getRoomTypeName(room)}
                                </span>
                              ) : null}
                            </span>
                          </div>
                        </div>
                        <hr className="py-0" />
                        <h5 className="tx-18 _600 text-body mb-2">
                          Complementaries
                        </h5>
                        <div className="d-flex flex-column gap-2 mb-3">
                          {selectedExtras.length > 0 ? (
                            <>
                              {selectedExtras
                                .slice(0, 2)
                                .map((comp: Complementary) => (
                                  <div
                                    key={comp.id}
                                    className="d-flex align-items-center"
                                  >
                                    <i className="bi bi-gift tx-18 text-success"></i>
                                    <span className="tx-13 px-2">
                                      {comp.name}
                                    </span>
                                  </div>
                                ))}
                              {selectedExtras.length > 2 ? (
                                <div>
                                  <button
                                    type="button"
                                    className="btn btn-link"
                                    onClick={() =>
                                      showComplementariesForRoom(room, idx)
                                    }
                                  >
                                    Show more
                                  </button>
                                </div>
                              ) : (
                                <span
                                  style={{
                                    height: 20,
                                    display: "block",
                                    visibility: "hidden",
                                  }}
                                >
                                  &nbsp;
                                </span>
                              )}
                            </>
                          ) : null}
                        </div>
                      </div>
                      <div>
                        {displayAmount !== null && isShowPrice && (
                          <div className="mb-2">
                            <small className="text-muted">Price:</small>
                            <div className="fw-bold">
                              {currencySymbol}{Number(displayAmount).toLocaleString()}
                            </div>
                          </div>
                        )}
                        {userRole !== "B2BUser" && (
                          <Button
                            label={
                              isReserved(room?.id)
                                ? "Already Reserved"
                                : isShortlisted(room?.id) || room?.isShortlisted
                                ? "In Shortlist"
                                : "Reserve"
                            }
                            className={`w-100 mt-2 ${
                              isReserved(room?.id)
                                ? "btn btn-success"
                                : isShortlisted(room?.id) || room?.isShortlisted
                                ? "btn btn-warning"
                                : "btn btn-primary"
                            }`}
                            onClick={() => handleReserve(idx)}
                            disabled={
                              saving === idx ||
                              isShortlisted(room?.id) ||
                              room?.isShortlisted ||
                              isReserved(room?.id)
                            }
                            loading={saving === idx}
                          />
                        )}
                        {userRole === "B2BUser" && (
                          <Button
                            label={
                              isReserved(room?.id)
                                ? "Already Booked"
                                : isShortlisted(room?.id) || room?.isShortlisted
                                ? "Remove from Shortlist"
                                : "Add to Shortlist"
                            }
                            className={`w-100 mt-2 ${
                              isReserved(room?.id)
                                ? "btn btn-secondary"
                                : isShortlisted(room?.id) || room?.isShortlisted
                                ? "btn btn-success"
                                : "btn btn-warning"
                            }`}
                            icon={
                              isShortlisted(room?.id) || room?.isShortlisted
                                ? "pi pi-heart-fill"
                                : "pi pi-heart"
                            }
                            onClick={() => handleShortlist(idx)}
                            disabled={
                              shortlistSaving === idx || isReserved(room?.id)
                            }
                            loading={shortlistSaving === idx}
                          />
                        )}
                      </div>
                    </div>
                  </article>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>

      {/* Modal for Complementaries */}
      {complementariesModal.visible && (
        <Dialog
          header="Complementaries"
          visible={complementariesModal.visible}
          style={{ width: "50vw" }}
          onHide={() =>
            setComplementariesModal({ visible: false, complementaries: [] })
          }
          draggable={false}
          resizable={false}
          modal
        >
          <div>
            <div className="row">
              {complementariesModal.complementaries.map((comp: any) => (
                <div className="col-md-4 mb-3" key={comp.id}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-gift tx-18 text-success"></i>
                    <span className="tx-13 px-2">{comp.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Dialog>
      )}

      {/* Modal for Reservation - Adults/Children Input */}
      {reservationModal.visible && (
        <Dialog
          header="Complete Reservation"
          visible={reservationModal.visible}
          style={{ width: "400px" }}
          onHide={() =>
            setReservationModal({
              visible: false,
              roomIdx: -1,
              noOfAdults: 1,
              noOfChildrens: 0,
              checkIn: checkIn || "",
              checkOut: checkOut || "",
            })
          }
          draggable={false}
          resizable={false}
          modal
        >
          <div className="p-3">
            <div className="mb-3">
              <strong>Room:</strong>{" "}
              {rooms[reservationModal.roomIdx]?.name || "Selected Room"}
            </div>

            {/* Show prefilled message for B2B users */}
            {(noOfAdults || noOfChildrens) && userRole === "B2BUser" && (
              <div className="mb-3 p-2 bg-light rounded">
                <small className="text-info">
                  <i className="pi pi-info-circle me-1"></i>
                  Values are prefilled from your profile. You can edit them if
                  needed.
                </small>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label fw-semibold">
                Check-In Date <span className="text-danger">*</span>
              </label>
              <InputText
                type="date"
                className="w-100"
                value={reservationModal.checkIn}
                onChange={(e) =>
                  setReservationModal((prev) => ({
                    ...prev,
                    checkIn: e.target.value,
                  }))
                }
                placeholder="Check-In Date"
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">
                Check-Out Date <span className="text-danger">*</span>
              </label>
              <InputText
                type="date"
                className="w-100"
                value={reservationModal.checkOut}
                onChange={(e) =>
                  setReservationModal((prev) => ({
                    ...prev,
                    checkOut: e.target.value,
                  }))
                }
                placeholder="Check-Out Date"
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">
                Number of Adults <span className="text-danger">*</span>
              </label>
              <InputText
                type="number"
                className="w-100"
                min="1"
                value={
                  reservationModal.noOfAdults?.toString() ??
                  (noOfAdults?.toString() || "1")
                }
                onChange={(e) =>
                  setReservationModal((prev) => ({
                    ...prev,
                    noOfAdults: Math.max(1, parseInt(e.target.value) || 1),
                  }))
                }
                placeholder="Number of adults"
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">
                Number of Children
              </label>
              <InputText
                type="number"
                className="w-100"
                min="0"
                value={
                  reservationModal.noOfChildrens?.toString() ??
                  (noOfChildrens?.toString() || "0")
                }
                onChange={(e) =>
                  setReservationModal((prev) => ({
                    ...prev,
                    noOfChildrens: Math.max(0, parseInt(e.target.value) || 0),
                  }))
                }
                placeholder="Number of children"
              />
            </div>

            <div className="d-flex gap-2 mt-4">
              <Button
                label="Cancel"
                className="btn btn-secondary w-50"
                onClick={() =>
                  setReservationModal({
                    visible: false,
                    roomIdx: -1,
                    noOfAdults: 1,
                    noOfChildrens: 0,
                    checkIn: checkIn || "",
                    checkOut: checkOut || "",
                  })
                }
              />
              <Button
                label="Confirm Reservation"
                className="btn btn-primary w-50"
                onClick={handleConfirmReservation}
                disabled={saving === reservationModal.roomIdx}
                loading={saving === reservationModal.roomIdx}
              />
            </div>
          </div>
        </Dialog>
      )}

      {/* Room Gallery Modal */}
      <GalleryModal
        visible={roomGalleryModal.visible}
        onHide={closeRoomGallery}
        images={roomGalleryModal.images}
        initialImageIndex={roomGalleryModal.initialImageIndex}
        data={roomGalleryModal.roomData}
        mode="room"
      />
    </>
  );
};

export default PropertyRoomDetail;
